import React, { FC, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { Helmet } from "react-helmet-async";

import { resetPassword } from "../../services/firebase";
import { resetPasswordFormAtom } from "../../state/atoms";
import { canSubmitResetpasswordFormSelector } from "../../state/selectors";
import { Errors } from "../../types/errors";
import { useAuthCrm } from "../../components/CrmUi/authCrm";
import { AuthButton, AuthError, AuthField, AuthLink, AuthPanel, AuthShell } from "./AuthUi";

/** Reset password, in the look of the CRM the person came for (authCrm.ts). */
const ResetPassword: FC = () => {
  const crm = useAuthCrm();
  const isHubSpot = crm === "hubspot";
  const [resetPasswordForm, setResetPasswordForm] = useRecoilState(
    resetPasswordFormAtom
  );
  const canSubmitResetPasswordForm = useRecoilValue(
    canSubmitResetpasswordFormSelector
  );

  const [isProcessing, setIsProcessing] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (canSubmitResetPasswordForm) {
      try {
        setIsProcessing(true);
        setError("");
        await resetPassword(resetPasswordForm.email);
        setIsEmailSent(true);
      } catch (error) {
        setError(
          `Error Resetting password (Code ${Errors.AUTH_RESET_PASSWORD_FAILURE}). Please validate the data you have entered, and try again.`
        );
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const backToSignIn = (
    <p className={isHubSpot ? "ba-switch" : "ba-secondary"}>
      <AuthLink crm={crm} to="/auth/login" ink={isHubSpot}>
        Back to sign in
      </AuthLink>
    </p>
  );

  return (
    <AuthShell crm={crm} illustration>
      <Helmet>
        <title>Reset password | boring</title>
      </Helmet>
      {isEmailSent ? (
        <AuthPanel
          crm={crm}
          title="Check your email"
          intro={
            <>
              We sent a reset password email to <strong>{resetPasswordForm.email}</strong>.
              Click the link in it to set your new password.
            </>
          }
        >
          {backToSignIn}
        </AuthPanel>
      ) : (
        <AuthPanel
          crm={crm}
          title={isHubSpot ? "Reset your password" : "Reset password"}
          intro="Enter the email you sign in with and we'll email you a link to set a new password."
          onSubmit={onSubmit}
        >
          <AuthField
            crm={crm}
            label={isHubSpot ? "Email" : "Email address"}
            type="email"
            name="email"
            autoComplete="username"
            autoFocus
            placeholder={isHubSpot ? "name@company.com" : undefined}
            value={resetPasswordForm.email}
            onChange={(email) => setResetPasswordForm({ ...resetPasswordForm, email })}
          />
          <AuthButton
            type="submit"
            loading={isProcessing}
            busyLabel="Sending the email"
            disabled={!canSubmitResetPasswordForm}
          >
            {isHubSpot ? "Send reset link" : "Reset password"}
          </AuthButton>
          <AuthError>{error}</AuthError>
          {backToSignIn}
        </AuthPanel>
      )}
    </AuthShell>
  );
};

export default ResetPassword;
